import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { WorkPeriod } from '../../api/work-periods-client/work-periods-client.type';
import { deduplicateComboboxGroups } from '../../helpers/general-helpers';
import { useTeamsInitializer } from '../app/app.hooks';
import {
  getBoardOptions,
  useBoardManagement,
  useBoards,
  useTeams,
  useWorkPeriodsData,
  useWorkPeriodsViewData,
} from './hooks/work-periods.hooks';
import {
  type FlowData,
  type HealthScoreData,
  type KeyProcessMeasuresData,
  type WorkPeriodsContextType,
  WorkPeriodType,
} from './work-periods.type';

const initialState: WorkPeriodsContextType = {
  selectedWorkPeriodType: WorkPeriodType.Defined,
  selectedWorkPeriods: [],
  dateRange: {
    startDate: null,
    endDate: null,
  },
  isLoading: false,
  isFetching: false,
  scoreData: undefined,
  keyMeasuresData: undefined,
  flowData: undefined,
  customMeasures: [],
  measureData: {},
  selectedBoardId: null,
  teamId: null,
  formattedBoards: [],
  tasksByPhase: {},
  tasksByMeasure: {},
  tasksByDate: {},
  workPeriods: [],
  setTeamId: () => {},
  setSelectedBoardId: () => {},
  setSelectedWorkPeriodType: () => {},
  setSelectedWorkPeriods: () => {},
  setDateRange: () => {},
  fetchWorkPeriods: async () => {},
  fetchWorkPeriodData: async () => {},
  handleBoardSelect: () => {},
  handleWorkPeriodTypeSelect: () => {},
  handleDateRangeChange: () => {},
  handleAdHocSubmit: () => {},
  handleWorkPeriodSelect: () => {},
};

const WorkPeriodsContext = createContext<WorkPeriodsContextType>(initialState);

export function WorkPeriodsProvider({ children }: { children: ReactNode }) {
  // 1. All useState hooks first
  const [selectedWorkPeriodType, setSelectedWorkPeriodType] = useState<WorkPeriodType>(WorkPeriodType.Defined);
  const [selectedWorkPeriods, setSelectedWorkPeriods] = useState<WorkPeriod[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>('1');
  const [_workPeriods, _setWorkPeriods] = useState<WorkPeriod[]>([]);

  const { isFetching: isFetchingTeams } = useTeamsInitializer();

  // 2. Get teams from global store
  const teams = useTeams();

  // 3. Use board management hook (auto-select default board only for Pre-defined type)
  useBoardManagement(selectedBoardId, setSelectedBoardId, selectedWorkPeriodType === WorkPeriodType.Defined);

  // 4. Fetch all data
  const { isLoading: isLoadingBoards } = useBoards(teamId);

  const {
    data: workPeriods,
    isLoading: isLoadingWorkPeriods,
    isFetching: isFetchingWorkPeriods,
  } = useWorkPeriodsData(selectedBoardId);

  const {
    scoreData: apiScoreData,
    keyMeasuresData: apiKeyMeasuresData,
    flowData: apiFlowData,
    customMeasures: apiCustomMeasures,
    measureData: apiMeasureData,
    isLoading: isLoadingViewData,
  } = useWorkPeriodsViewData(selectedBoardId, selectedWorkPeriods, dateRange, selectedWorkPeriodType);

  // 7. Memoize derived values
  const formattedBoards = useMemo(() => {
    if (isFetchingTeams) {
      return [];
    }

    const boardOptions = getBoardOptions(teams);
    const deduplicated = deduplicateComboboxGroups(boardOptions);
    return deduplicated.flatMap((group) =>
      group.items.map((item) => {
        const comboItem = typeof item === 'string' ? { value: item, label: item } : item;
        return {
          value: comboItem.value as string,
          label: comboItem.label as string,
        };
      }),
    );
  }, [teams, isFetchingTeams]);

  // Auto-select a default board when landing on the page in Pre-defined mode
  useEffect(() => {
    if (selectedWorkPeriodType === WorkPeriodType.Defined && formattedBoards.length > 0) {
      const isCurrentBoardValid = selectedBoardId && formattedBoards.some((board) => board.value === selectedBoardId);

      if (!selectedBoardId || !isCurrentBoardValid) {
        const defaultBoard = formattedBoards[0];

        if (defaultBoard) {
          const boardChanged = selectedBoardId !== defaultBoard.value;

          if (boardChanged) {
            setSelectedBoardId(defaultBoard.value);
          }

          // Only reset when board actually changes or is initially null
          if (!selectedBoardId || boardChanged) {
            setSelectedWorkPeriods([]);
            setDateRange({ startDate: null, endDate: null });
          }
        }
      }
    }
  }, [selectedWorkPeriodType, selectedBoardId, formattedBoards]);

  const scoreData = useMemo(() => apiScoreData as unknown as HealthScoreData, [apiScoreData]);
  const keyMeasuresData = useMemo(() => apiKeyMeasuresData as KeyProcessMeasuresData, [apiKeyMeasuresData]);
  const flowData = useMemo(() => apiFlowData as FlowData, [apiFlowData]);
  const customMeasures = useMemo(() => apiCustomMeasures || [], [apiCustomMeasures]);
  const measureData = useMemo(() => apiMeasureData || {}, [apiMeasureData]);

  const isLoading = useMemo(
    () => isLoadingBoards || isLoadingWorkPeriods || isLoadingViewData,
    [isLoadingBoards, isLoadingWorkPeriods, isLoadingViewData],
  );

  const isFetching = isFetchingWorkPeriods || isFetchingTeams;

  /**
   * Fetches work periods for the selected board.
   * This function is called when the user wants to load work periods.
   * @param boardId - The ID of the board to fetch work periods for
   */
  const fetchWorkPeriods = useCallback(
    async (boardId?: string | null) => {
      // Use the provided boardId or the current selectedBoardId
      const boardToUse = boardId !== undefined ? boardId : selectedBoardId;

      // Update the selectedBoardId if a new boardId is provided
      if (boardId !== undefined && boardId !== selectedBoardId) {
        setSelectedBoardId(boardId);
      }

      // Reset work period selection when board changes
      setSelectedWorkPeriods([]);

      // If we have work periods data from the API and it's for the current board, update the selected work periods
      if (workPeriods && workPeriods.length > 0 && boardToUse === selectedBoardId) {
        // Use the first work period from the same array that feeds the dropdown
        setSelectedWorkPeriods([workPeriods[0]]);
      }
    },
    [selectedBoardId, workPeriods],
  );

  /**
   * Fetches data for the selected work periods.
   * This function is called when the user selects work periods to analyze.
   * The actual data fetching is handled by the hooks based on the selected work periods.
   */
  const fetchWorkPeriodData = useCallback(async () => {
    // No explicit action needed here as the data fetching is reactive
    // based on the selectedWorkPeriods, dateRange, and selectedWorkPeriodType
  }, []);

  /**
   * Handle board selection
   * @param boardId The board ID
   */
  const handleBoardSelect = useCallback(
    (boardId: string | null) => {
      setSelectedBoardId(boardId);
      setSelectedWorkPeriods([]);
      // Reset work period selection when board changes
      if (boardId) {
        fetchWorkPeriods(boardId);
      }
    },
    [fetchWorkPeriods],
  );

  /**
   * Handle work period type selection
   * @param type The work period type
   */
  const handleWorkPeriodTypeSelect = useCallback((type: WorkPeriodType) => {
    setSelectedWorkPeriodType(type);
    setSelectedWorkPeriods([]);
    // If switching to Custom (ad-hoc), clear selected board to force manual selection
    if (type === WorkPeriodType.Custom) {
      setSelectedBoardId(null);
    }
  }, []);

  /**
   * Handle date range selection for ad-hoc work periods
   * @param range The date range
   */
  const handleDateRangeChange = useCallback((range: { startDate: Date | null; endDate: Date | null }) => {
    setDateRange(range);
  }, []);

  /**
   * Handle ad-hoc work period submission
   */
  const handleAdHocSubmit = useCallback(() => {
    // Validate before enabling fetch
    if (!selectedBoardId) {
      console.error('[Context] Ad-hoc submission blocked: No board selected');
      return;
    }

    if (!dateRange.startDate || !dateRange.endDate) {
      console.error('[Context] Ad-hoc submission blocked: Date range incomplete');
      return;
    }

    if (dateRange.startDate >= dateRange.endDate) {
      console.error('[Context] Ad-hoc submission blocked: Invalid date range');
      return;
    }

    // All validations passed – fetch data for ad-hoc range
    fetchWorkPeriodData();
  }, [selectedBoardId, dateRange, fetchWorkPeriodData]);

  /**
   * Handle work period selection
   * @param workPeriod The work period to select
   * @param isSelected Whether the work period is selected
   */
  const handleWorkPeriodSelect = useCallback(
    (workPeriod: WorkPeriod, isSelected: boolean) => {
      if (isSelected) {
        // For assessment view, we only allow one work period at a time
        if (selectedWorkPeriods.length >= 7) {
          // For comparison view, we allow up to 7 work periods
          setSelectedWorkPeriods((prevSelected) => {
            // Remove the oldest work period and add the new one
            const newSelected = [...prevSelected];
            newSelected.shift(); // Remove the first element
            newSelected.push(workPeriod);
            return newSelected;
          });
        } else {
          setSelectedWorkPeriods((prevSelected) => [...prevSelected, workPeriod]);
        }
      } else {
        // Remove the work period from the selection
        setSelectedWorkPeriods((prevSelected) => prevSelected.filter((wp) => wp.id !== workPeriod.id));
      }
    },
    [selectedWorkPeriods],
  );

  return (
    <WorkPeriodsContext.Provider
      value={{
        selectedWorkPeriodType,
        selectedWorkPeriods,
        dateRange,
        isLoading,
        isFetching,
        scoreData,
        keyMeasuresData,
        flowData,
        customMeasures,
        measureData,
        selectedBoardId,
        teamId,
        formattedBoards,
        workPeriods: workPeriods || [],
        setTeamId,
        setSelectedBoardId,
        setSelectedWorkPeriodType,
        setSelectedWorkPeriods,
        setDateRange,
        fetchWorkPeriods,
        fetchWorkPeriodData,
        handleBoardSelect,
        handleWorkPeriodTypeSelect,
        handleDateRangeChange,
        handleAdHocSubmit,
        handleWorkPeriodSelect,
      }}
    >
      {children}
    </WorkPeriodsContext.Provider>
  );
}

export function useWorkPeriodsContext() {
  return useContext(WorkPeriodsContext);
}
